import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { SignInSource } from '@ninety/mobile/platform/auth';

import { appActions } from '../../app/app.actions';

import { SessionSelectors } from './session.selectors';

/**
 * The password of the sign-in that just happened, held until someone answers the enrollment offer.
 *
 * **Why it is here rather than on the state or on an event.** Enrolling needs a plaintext password and
 * one exists only during a sign-in, so something has to carry it from the sign-in to the offer that
 * follows. Carrying it on an `AuthEvent` would put it through the reducer, into every dispatched action,
 * and into `describeState`; carrying it on `AuthState` would do the same to every state description. A
 * service is the one place it can sit without weakening that guarantee — nothing here is serialised,
 * logged, or persisted, and the offer component reads {@link has} rather than the pair.
 *
 * **Holding it is what lets the offer ask about the sign-in rather than about the device.** Asking the
 * device — "is a password stored here?" — is answered by whichever sign-in wrote last rather than by the
 * one being offered on, and a device accumulates sign-ins: one password sign-in makes that answer yes for
 * good. A federated sign-in supplies none, so on that question it would be offered an enrollment against
 * a credential that had nothing to do with it, and the button would replay that credential rather than
 * the account the session was established with. "Is one in hand?" is false for a federated sign-in by
 * construction. ADR 0003 § The trade has the argument.
 *
 * ## Its lifetime
 *
 * Three things end it, listed here so that "how long can a plaintext password live?" has one place to
 * be answered even though not every one of them fires from here:
 *
 * 1. **An answer to the offer**, which is the caller's to signal, because only the caller knows an
 *    answer was given. Accepting {@link take}s the pair — `SessionBiometricsEffects` needs its value —
 *    and the other two answers {@link discard} it from `BiometricOfferComponent`. An acceptance the
 *    machine *refuses* deliberately clears nothing: the sheet stays up, so the tap can be made again.
 * 2. **The machine reaching `unauthenticated`**, which is every way a session can end. Enforced here.
 * 3. **The app leaving the foreground**, so an unanswered question leaves nothing behind — including
 *    the case where the app is killed rather than resumed. Enforced here, whether or not the offer was
 *    ever on screen. {@link appActions.appBackgrounded} is broader than a background on iOS, and
 *    discarding on one of those interruptions is the safe way to be wrong.
 *
 * A path that holds the pair for longer than one of these belongs in this list or nowhere.
 */
@Injectable({ providedIn: 'root' })
export class PendingCredentials {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  private credentials: { email: string; password: string } | null = null;

  /**
   * Which way in the Grant currently running is, read off the machine.
   *
   * Only {@link hold} consults it, and only to refuse a credential replay: a replay produces a
   * password exactly as a typed sign-in does, and there is nothing to offer behind it because an
   * enrollment already exists. Taken from the state rather than from the Command, because the Command
   * is the same `srpSignIn` either way — `authorizing.source` is where the difference is recorded.
   */
  private source: SignInSource | null = null;

  constructor() {
    this.store
      .select(SessionSelectors.state)
      .pipe(takeUntilDestroyed())
      .subscribe(state => {
        if (state.tag === 'authorizing') this.source = state.source;
        if (state.tag === 'unauthenticated') this.discard();
      });

    this.actions$.pipe(ofType(appActions.appBackgrounded), takeUntilDestroyed()).subscribe(() => this.discard());
  }

  /**
   * Keep the password a password sign-in just used, for the offer that follows.
   *
   * A no-op for anything else, which today means a credential replay. It is deliberately silent rather
   * than a throw: the caller is one handler serving both, and the difference is not its business.
   */
  hold(email: string, password: string): void {
    if (this.source !== 'password') return;
    this.credentials = { email, password };
  }

  /** Whether there is a password in hand. The offer's condition, and all of this the offer may see. */
  has(): boolean {
    return this.credentials !== null;
  }

  /** The credentials, and clear them. `null` when nothing is held. */
  take(): { email: string; password: string } | null {
    const credentials = this.credentials;
    this.credentials = null;
    return credentials;
  }

  /** Forget them, having neither stored them nor been asked to. */
  discard(): void {
    this.credentials = null;
  }
}
